/**
 * SavedAddressPicker — lets a signed-in pet owner drop one of their saved
 * addresses into any address form (service pickups/visits and delivery
 * checkouts). On first load it auto-applies the user's default (or first)
 * saved address so the common case needs zero typing; chips switch between
 * saved addresses, and "New address" clears the fields to type a fresh one.
 *
 * Purely presentational glue: the parent owns the form state and maps the
 * chosen `UserAddress` onto its own fields via `on_select` (and blanks them
 * via `on_clear`). Renders nothing when the user has no saved addresses, so
 * forms behave exactly as before for those users.
 */
import { useEffect, useRef, useState } from 'react';
import { MapPin, Plus } from 'lucide-react';
import { use_addresses } from '@/features/user-address/hooks';
import type { UserAddress } from '@/features/user-address/api';

export function SavedAddressPicker({
  on_select,
  on_clear,
  auto_apply_default = true,
}: {
  /** Apply a saved address to the form fields. */
  on_select: (address: UserAddress) => void;
  /** Clear the address fields so the user can enter a new one. Optional. */
  on_clear?: () => void;
  /** Prefill the default (or first) saved address once on load. */
  auto_apply_default?: boolean;
}) {
  const { data: addresses } = use_addresses();
  // 'new' = user chose to type a fresh address; string = a saved address id.
  const [selected_id, set_selected_id] = useState<string | null>(null);
  const applied = useRef(false);

  useEffect(() => {
    if (applied.current || !auto_apply_default || !addresses || addresses.length === 0) return;
    applied.current = true;
    const preferred = addresses.find((a) => a.is_default) ?? addresses[0];
    set_selected_id(preferred.id);
    on_select(preferred);
    // on_select is a fresh closure each render; the ref guard makes this run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addresses, auto_apply_default]);

  if (!addresses || addresses.length === 0) return null;

  const line = (a: UserAddress) => [a.address, a.city].filter(Boolean).join(', ');

  return (
    <div className="flex flex-col gap-2" data-testid="saved-address-picker">
      <span className="text-xs font-medium text-muted-foreground">Use a saved address</span>
      <div className="flex flex-wrap gap-2">
        {addresses.map((a) => {
          const active = selected_id === a.id;
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => {
                set_selected_id(a.id);
                on_select(a);
              }}
              data-testid={`saved-address-${a.id}`}
              title={line(a)}
              className={`inline-flex max-w-[15rem] items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-xs font-semibold transition-all duration-fast active:scale-95 ${
                active
                  ? 'border-primary bg-primary/10 text-primary shadow-clay-sm'
                  : 'border-input text-muted-foreground hover:border-primary/50 hover:bg-muted'
              }`}
            >
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{a.label}</span>
              {a.is_default && <span className="shrink-0 opacity-70">· default</span>}
            </button>
          );
        })}
        {on_clear && (
          <button
            type="button"
            onClick={() => {
              set_selected_id('new');
              on_clear();
            }}
            data-testid="saved-address-new"
            className={`inline-flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-xs font-semibold transition-all duration-fast active:scale-95 ${
              selected_id === 'new'
                ? 'border-primary bg-primary/10 text-primary shadow-clay-sm'
                : 'border-dashed border-input text-muted-foreground hover:border-primary/50 hover:bg-muted'
            }`}
          >
            <Plus className="h-3.5 w-3.5" /> New address
          </button>
        )}
      </div>
    </div>
  );
}
